import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useIsDesktop } from './useMedia';

export interface CarouselItem {
  key: string;
  /** Optional label shown in aria-label of the pagination dot. */
  label?: string;
  render: () => ReactNode;
}

interface Props {
  items: CarouselItem[];
  /**
   * A value that, when changed, resets scroll to the first panel.
   * Useful when the carousel shows content for a different entity (e.g. a different run).
   */
  resetKey?: string | number;
  /**
   * How to render on desktop:
   *   - 'stack' (default): vertical stack with gap, natural content sizing.
   *   - 'carousel': horizontal snap carousel with pagination dots (same as mobile).
   *   - 'scroll-list': vertical scroll list; items render at their own height, no gap.
   */
  desktopMode?: 'stack' | 'carousel' | 'scroll-list';
  /** Called when the active (centered) item changes. Only fires in carousel modes. */
  onActiveChange?: (index: number, item: CarouselItem) => void;
}

/**
 * Mobile: horizontal snap carousel with pagination dots.
 * Desktop: depends on desktopMode (default: vertical stack).
 *
 * Each item controls its own content; the carousel just handles layout,
 * scroll tracking, and dots.
 */
export function Carousel({ items, resetKey, desktopMode = 'stack', onActiveChange }: Props) {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isDesktop = useIsDesktop();
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;

  // Reset to first panel when the reset key changes (e.g. switching selected run)
  useEffect(() => {
    setActive(0);
    scrollRef.current?.scrollTo({ left: 0, top: 0, behavior: 'auto' });
  }, [resetKey]);

  // Track the active panel while swiping
  const isCarouselMode = !isDesktop || desktopMode === 'carousel';
  useEffect(() => {
    if (!isCarouselMode) return;
    const el = scrollRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.carouselIdx);
            if (Number.isFinite(idx)) {
              setActive(idx);
              const item = items[idx];
              if (item) onActiveChangeRef.current?.(idx, item);
            }
          }
        });
      },
      { root: el, threshold: 0.6 }
    );
    el.querySelectorAll<HTMLElement>('[data-carousel-idx]').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [isCarouselMode, items.length, resetKey]);

  const scrollTo = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const target = el.querySelector<HTMLElement>(`[data-carousel-idx="${idx}"]`);
    target?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  // Desktop: vertical stack
  if (isDesktop && desktopMode === 'stack') {
    return (
      <div
        ref={scrollRef}
        className="flex-1 flex flex-col gap-6 overflow-y-auto px-6 pt-5 pb-6 min-h-0"
      >
        {items.map((it, i) => (
          <section key={it.key} data-carousel-idx={i}>
            {it.render()}
          </section>
        ))}
      </div>
    );
  }

  // Desktop: vertical scrolling list (items already card-styled)
  if (isDesktop && desktopMode === 'scroll-list') {
    return (
      <div
        ref={scrollRef}
        className="flex-1 flex flex-col gap-2 overflow-y-auto px-4 py-4 min-h-0"
      >
        {items.map((it, i) => (
          <div key={it.key} data-carousel-idx={i}>
            {it.render()}
          </div>
        ))}
      </div>
    );
  }

  // Mobile (or desktopMode='carousel'): horizontal snap carousel with dots
  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 flex flex-row min-h-0 min-w-0 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth scroll-px-3 px-3 pt-4 pb-3"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((it, i) => (
          <section
            key={it.key}
            data-carousel-idx={i}
            className="shrink-0 snap-center w-[calc(100%-0.5rem)] mx-1 flex flex-col min-h-0"
          >
            {it.render()}
          </section>
        ))}
      </div>

      {items.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 pb-3 pt-1">
          {items.map((it, i) => (
            <button
              key={it.key}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={it.label ? `Go to ${it.label}` : `Panel ${i + 1}`}
              aria-current={active === i ? 'true' : undefined}
              className={[
                'transition-all duration-200 rounded-full',
                active === i
                  ? 'w-5 h-1.5 bg-ink-900'
                  : 'w-1.5 h-1.5 bg-ink-300 hover:bg-ink-500'
              ].join(' ')}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Standard section: small uppercase label + rounded-border card.
 * Used inside Carousel items that have a title + content (e.g. detail charts).
 *
 * Renders differently based on the layout context:
 *   - `fill=true` (mobile carousel): card fills available panel height.
 *   - `fill=false` (desktop stack): card sizes to its content.
 */
export function SectionCard({
  title,
  fill = false,
  scrollable = false,
  children
}: {
  title: string;
  fill?: boolean;
  scrollable?: boolean;
  children: ReactNode;
}) {
  if (!fill) {
    return (
      <>
        <div className="stat-label mb-2">{title}</div>
        <div className="rounded-2xl border border-ink-100 bg-white/60 p-3">{children}</div>
      </>
    );
  }

  return (
    <>
      <div className="stat-label mb-2 px-1">{title}</div>
      <div
        className={[
          'rounded-2xl border border-ink-100 bg-white/60 p-4',
          'flex-1 flex flex-col justify-center min-h-0',
          scrollable ? '!justify-start overflow-y-auto' : ''
        ].join(' ')}
      >
        {children}
      </div>
    </>
  );
}
